import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, BadgeCheck } from "lucide-react";
import api from "@/lib/api";
import { usePageMeta } from "@/lib/seo";
import { priceParts } from "@/lib/pricing";
import NavTop from "@/components/nav/nav_top";

// A single host's public profile: headshot, name, badge and all of their
// active listings, reached from the Hosts directory or a listing attribution.
// Backed by GET /api/listers/{id}/public. Honest not-found state.
export default function ListerPublicPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lister, setLister] = useState(null);
  const [notFound, setNotFound] = useState(false);

  // SEO: upgrade the generic route title with the host's real name.
  usePageMeta(
    lister
      ? {
          title: `${lister.name} | Homes for Rent`,
          description: `Browse active rental listings from ${lister.name} on SwipRent.`,
        }
      : null
  );

  useEffect(() => {
    let alive = true;
    setNotFound(false);
    api
      .get(`/listers/${id}/public`)
      .then((res) => alive && setLister(res.data?.lister || null))
      .catch(() => alive && setNotFound(true));
    return () => { alive = false; };
  }, [id]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-night">
        <NavTop />
        <main className="mx-auto flex max-w-md flex-col items-center px-6 pt-24 text-center">
          <h1 className="text-xl font-semibold text-night-text">We could not find this host</h1>
          <button
            type="button"
            onClick={() => navigate("/hosts")}
            className="mt-6 rounded-full bg-brand-primary px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-brand-primary-hover"
          >
            Back to hosts
          </button>
        </main>
      </div>
    );
  }

  if (!lister) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-night">
        <div className="h-10 w-10 animate-spin rounded-full border-[3px] border-night-line border-t-brand-primary" />
      </div>
    );
  }

  const listings = lister.listings || [];

  return (
    <div className="min-h-screen bg-night pb-24 md:pb-10">
      <NavTop />
      <main className="mx-auto max-w-5xl px-4 pt-6 sm:px-6">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-night-sub transition hover:text-night-text"
        >
          <ArrowLeft size={15} /> Back
        </button>

        <div className="flex items-center gap-4">
          {lister.photo_url ? (
            <img src={lister.photo_url} alt={lister.name} className="h-20 w-20 rounded-full object-cover" />
          ) : (
            <span className="flex h-20 w-20 items-center justify-center rounded-full bg-brand-primary text-2xl font-semibold text-white">
              {(lister.name || "?").slice(0, 1).toUpperCase()}
            </span>
          )}
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight text-night-text">
              {lister.name || "Host"}
              {lister.verified && <BadgeCheck size={20} className="text-brand-primary" />}
            </h1>
            <p className="mt-0.5 text-sm text-night-sub">
              {lister.is_realtor ? "Licensed realtor" : "Private host"}
              {lister.brokerage_name ? ` · ${lister.brokerage_name}` : ""}
              {" · "}
              {lister.listing_count} active home{lister.listing_count === 1 ? "" : "s"}
            </p>
          </div>
        </div>

        <h2 className="mt-8 text-lg font-semibold text-night-text">Their homes</h2>
        {listings.length === 0 ? (
          <p className="mt-2 text-sm text-night-sub">No active listings right now.</p>
        ) : (
          <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 lg:grid-cols-4">
            {listings.map((l) => (
              <Link key={l.id} to={`/listing/${l.id}`} className="group">
                <div className="overflow-hidden rounded-2xl">
                  <img
                    src={l.photos?.[0]}
                    alt={l.title}
                    loading="lazy"
                    className="aspect-[4/3] w-full object-cover transition group-hover:scale-[1.02]"
                  />
                </div>
                <p className="mt-2 truncate text-sm font-semibold text-night-text">{l.title}</p>
                <p className="text-xs text-night-sub">
                  ${priceParts(l).amount.toLocaleString()} {priceParts(l).unit}
                </p>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
